// Signed-in user, their staff permissions and their homes.
import { api } from "./api";
import { applyLocale, type Locale } from "./i18n";
import { dropResources, resetLocal, store } from "./ui";

export interface Me {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  locale: Locale;
  staffRole: { key: string; name: { en: string; fr: string } } | null;
  permissions: string[];
  mustChangePassword?: boolean;
}

export interface MyHome {
  id: string;
  code: string;
  nickname: string | null;
  address_line1: string;
  city: string;
  building_type: string;
  health: "good" | "attention" | "critical";
  roles: string[];
  status: string;
}

export const session = store({ loaded: false, me: null as Me | null, homes: [] as MyHome[] });

export function can(perm: string) {
  const p = session.me?.permissions ?? [];
  return p.includes("*") || p.includes(perm);
}

export function canAny(...perms: string[]) {
  return perms.some(can);
}

export const ADMIN_PERMS = [
  "admin.brand", "admin.settings", "admin.roles", "admin.staff", "admin.users", "admin.audit",
  "ops.homes", "ops.members", "ops.tickets", "ops.visits", "ops.partners", "ops.checklists",
];

export function isAdminUser() {
  return canAny(...ADMIN_PERMS);
}

export function setSession(data: { user: Me | null; homes?: MyHome[] }) {
  session.set({ loaded: true, me: data.user, homes: data.homes ?? [] });
  if (data.user?.locale) applyLocale(data.user.locale);
}

export async function loadSession() {
  try {
    setSession(await api.get("/api/me"));
  } catch {
    session.set({ loaded: true, me: null, homes: [] });
  }
}

export async function signOut() {
  await api.post("/api/auth/logout").catch(() => {});
  dropResources();
  resetLocal("");
  session.set({ me: null, homes: [] });
}

/** Where a user lands after signing in. */
export function homePath(): string {
  if (session.homes.length) return "/";
  if (isAdminUser()) return "/admin";
  if (can("field.visits")) return "/field";
  return "/";
}
